import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RelationshipEditor from '../Common/RelationshipEditor';
import RelationshipDisplay from '../Common/RelationshipDisplay';
import RelationshipGraph from '../Common/RelationshipGraph';
import AttachmentManager from '../Common/AttachmentManager';
import ImageAttachmentDisplay from '../Common/ImageAttachmentDisplay';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Detail view for a location entry with relationships
 */
const LocationDetailPage = ({ entryId, onBack, onEdit, onDelete }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadEntry = useCallback(async () => {
    try {
      setLoading(true);
      const data = await wikiService.getWikiEntry(entryId);
      setEntry(data);
    } catch (error) {
      console.error('Error loading entry:', error);
    } finally {
      setLoading(false);
    }
  }, [entryId]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  // Scroll to top when component mounts or entryId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [entryId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!entry) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Entry not found</Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>
          <ArrowBackIcon sx={{ mr: 1 }} />
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: wikiTheme.dark, p: 2, borderRadius: 1 }}>
      <Button 
        onClick={onBack} 
        sx={{ mb: 2, color: wikiTheme.primary, fontFamily: wikiTheme.font }}
      >
        <ArrowBackIcon sx={{ mr: 1 }} />
        Back to Locations
      </Button>

      <Paper sx={{ p: 3, backgroundColor: wikiTheme.mediumGray }}>
        <Typography variant="h4" sx={{ mb: 2, color: wikiTheme.secondary, fontFamily: wikiTheme.font }}>
          📍 {entry.title}
        </Typography>

        {entry.tags?.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {entry.tags.map((tag) => {
              const isPlayerCharacter = tag === 'Player Character';
              return (
                <Chip 
                  key={tag} 
                  label={tag}
                  sx={{
                    backgroundColor: isPlayerCharacter ? '#4a90e2' : wikiTheme.primary,
                    color: isPlayerCharacter ? '#ffffff' : wikiTheme.dark,
                    fontFamily: wikiTheme.font,
                    fontWeight: isPlayerCharacter ? 'bold' : 'normal',
                    fontSize: isPlayerCharacter ? '0.9rem' : '0.8125rem',
                    border: isPlayerCharacter ? '2px solid #ffffff' : 'none',
                    boxShadow: isPlayerCharacter ? '0 0 8px rgba(74, 144, 226, 0.6)' : 'none',
                    pointerEvents: 'none'
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Image Attachments and Description - side by side for single image, stacked for multiple */}
        {entry.attachments && entry.attachments.length > 0 && entry.attachments.some(att => att.type && att.type.startsWith('image/')) ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: entry.attachments.filter(att => att.type && att.type.startsWith('image/')).length === 1 ? 'row' : 'column' },
            gap: 3,
            mb: 3 
          }}>
            <Box
              sx={{
                flex: 1,
                p: 2,
                bgcolor: wikiTheme.dark,
                borderRadius: 1,
                color: wikiTheme.secondary,
                fontFamily: wikiTheme.font,
                borderLeft: `3px solid ${wikiTheme.primary}`,
                order: { xs: 2, md: 2 }
              }}
              dangerouslySetInnerHTML={{ __html: entry.description }}
            />
            <Box sx={{ 
              flex: entry.attachments.filter(att => att.type && att.type.startsWith('image/')).length === 1 ? '0 0 auto' : '1',
              minWidth: { xs: '100%', md: entry.attachments.filter(att => att.type && att.type.startsWith('image/')).length === 1 ? '300px' : 'auto' },
              maxWidth: { xs: '100%', md: entry.attachments.filter(att => att.type && att.type.startsWith('image/')).length === 1 ? '400px' : '100%' },
              order: { xs: 1, md: 1 }
            }}>
              <ImageAttachmentDisplay attachments={entry.attachments} />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: wikiTheme.dark,
              borderRadius: 1,
              color: wikiTheme.secondary,
              fontFamily: wikiTheme.font,
              borderLeft: `3px solid ${wikiTheme.primary}`,
            }}
            dangerouslySetInnerHTML={{ __html: entry.description }}
          />
        )}

        {/* Two column layout for relationships and graph */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mb: 3
        }}>
          {/* Left column - Relationships list and editor */}
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  color: wikiTheme.primary, 
                  fontFamily: wikiTheme.font,
                  fontWeight: 'bold' 
                }}
              >
                Relationships
              </Typography>
              <RelationshipDisplay 
                relationships={entry.relationships || []} 
                entryId={entryId}
                onRelationshipChange={loadEntry}
              />
            </Box>

            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  color: wikiTheme.primary, 
                  fontFamily: wikiTheme.font,
                  fontWeight: 'bold' 
                }}
              >
                Manage Relationships
              </Typography>
              <RelationshipEditor
                entryId={entryId}
                currentRelationships={entry.relationships || []}
                onRelationshipChange={loadEntry}
                entryType="location"
              />
            </Box>
          </Box>

          {/* Right column - Relationship graph */}
          <Box>
            {(entry.relationships && entry.relationships.length > 0) && (
              <Accordion 
                defaultExpanded
                sx={{
                  backgroundColor: wikiTheme.dark,
                  '&:before': {
                    display: 'none',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: wikiTheme.primary }} />}
                  sx={{
                    backgroundColor: wikiTheme.mediumGray,
                    borderBottom: `2px solid ${wikiTheme.primary}`,
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                    },
                  }}
                >
                  <Typography 
                    sx={{ 
                      color: wikiTheme.primary, 
                      fontFamily: wikiTheme.font,
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                    }}
                  >
                    Relationship Graph
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2, backgroundColor: wikiTheme.dark }}>
                  <RelationshipGraph
                    entryId={entryId}
                    entryTitle={entry.title}
                    entryType="location"
                    relationships={entry.relationships || []}
                  />
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </Box>

        {/* Attachments Section */}
        <Box sx={{ mt: 3, mb: 3 }}>
          <AttachmentManager
            entryId={entryId}
            attachments={entry.attachments || []}
            onAttachmentsChange={loadEntry}
            readOnly={false}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button 
            variant="contained" 
            onClick={onEdit}
            sx={{
              backgroundColor: wikiTheme.primary,
              color: wikiTheme.dark,
              fontFamily: wikiTheme.font,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e0a23d',
              },
            }}
          >
            Edit
          </Button>
          <Button 
            variant="contained" 
            onClick={onDelete}
            sx={{
              backgroundColor: '#ff6b6b',
              color: '#fff',
              fontFamily: wikiTheme.font,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#ff5252',
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default LocationDetailPage;
